import { useState, useMemo, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { CalendarIcon, Plus, X, Users, CreditCard, AlertCircle, AlertTriangle, ChevronLeft, ChevronRight, Check, DollarSign, Calculator, Wallet, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { validateEmail } from "@/lib/emailValidation";
import { InviteLinkSettings } from "@/components/InviteLinkSettings";
import { BankDetailsDialog, checkBankDetailsConfigured } from "@/components/BankDetailsDialog";
import { SUPPORTED_CURRENCIES } from "@/lib/currency";

const CURRENCIES = SUPPORTED_CURRENCIES.map(({ code, symbol, name }) => ({ code, symbol, name }));

interface PrefilledParticipant {
  email: string;
  name: string;
}

interface AddGroupPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizerEmail: string;
  organizerName?: string;
  prefilledTitle?: string;
  prefilledParticipants?: PrefilledParticipant[];
}

interface InvitedFriend {
  email: string;
  name: string;
  customAmount?: number;
}

// Wizard steps definition - 4 steps
const STEPS = [
  { id: 1, name: "Basics", description: "What & when" },
  { id: 2, name: "Split", description: "Who & how" },
  { id: 3, name: "Invite", description: "Add friends" },
  { id: 4, name: "Review", description: "Confirm" },
];

// Animation variants for step transitions
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

export function AddGroupPaymentDialog({
  open,
  onOpenChange,
  organizerEmail,
  organizerName,
  prefilledTitle,
  prefilledParticipants,
}: AddGroupPaymentDialogProps) {
  const navigate = useNavigate();
  
  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  
  // Form field state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState<Date | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [targetAmount, setTargetAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [splitType, setSplitType] = useState<"equal" | "custom" | "open">("equal");
  const [friendsToInvite, setFriendsToInvite] = useState<string>(""); // Number of friends (excluding organizer)
  const [newFriendCustomAmount, setNewFriendCustomAmount] = useState<string>("");
  const [invitedFriends, setInvitedFriends] = useState<InvitedFriend[]>([]);
  const [newFriendEmail, setNewFriendEmail] = useState("");
  const [newFriendName, setNewFriendName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get currency symbol
  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '₹';
  
  // Organizer participation state
  const [isOrganizerParticipating, setIsOrganizerParticipating] = useState(true);
  
  // Unsaved changes warning state
  const [showExitWarning, setShowExitWarning] = useState(false);
  
  // Invite link settings state
  const [inviteLinkEnabled, setInviteLinkEnabled] = useState(false);
  const [inviteLinkApprovalMode, setInviteLinkApprovalMode] = useState<"auto" | "manual">("auto");
  const [maxSelfInvites, setMaxSelfInvites] = useState<number | null>(null);
  
  // Email validation state
  const [emailError, setEmailError] = useState<string>("");
  
  // Bank details state - for professionals
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState<boolean | null>(null);
  
  // Check if bank details are configured when dialog opens
  useEffect(() => {
    if (open && organizerEmail) {
      checkBankDetailsConfigured(organizerEmail).then(setHasBankDetails);
    }
  }, [open, organizerEmail]);

  // Initialize prefilled data when dialog opens
  useEffect(() => {
    if (open && prefilledTitle) {
      setTitle(prefilledTitle);
    }
    if (open && prefilledParticipants && prefilledParticipants.length > 0) {
      // Filter out organizer email from prefilled participants
      const filteredParticipants = prefilledParticipants
        .filter(p => p.email.toLowerCase() !== organizerEmail.toLowerCase())
        .map(p => ({ email: p.email, name: p.name }));
      setInvitedFriends(filteredParticipants);
    }
  }, [open, prefilledTitle, prefilledParticipants, organizerEmail]);

  const hasUnsavedChanges = useMemo(() => {
    return title.trim() !== "" || 
           description.trim() !== "" || 
           targetAmount !== "" ||
           invitedFriends.length > 0;
  }, [title, description, targetAmount, invitedFriends.length]);

  // Calculate participant counts and per-person amounts
  // For equal split: friendsToInvite + organizer (if participating) = total participants
  const friendsCount = friendsToInvite ? parseInt(friendsToInvite) : 0;
  const totalParticipantCount = friendsCount + (isOrganizerParticipating ? 1 : 0);
  const actualInvitedCount = invitedFriends.length + (isOrganizerParticipating ? 1 : 0);
  
  // For equal split, use the expected total. For others, use actual invited count.
  const displayParticipantCount = splitType === 'equal' && totalParticipantCount > 0 
    ? totalParticipantCount 
    : actualInvitedCount;
    
  const perPersonAmount = targetAmount && displayParticipantCount > 0 && splitType === 'equal'
    ? (parseFloat(targetAmount) / displayParticipantCount).toFixed(2)
    : null;
    
  // For custom split, calculate sum of custom amounts
  const customAmountsTotal = splitType === 'custom' 
    ? invitedFriends.reduce((sum, f) => sum + (f.customAmount || 0), 0)
    : 0;

  // Real-time email validation
  const validateEmailInput = useCallback((email: string): string => {
    if (!email.trim()) return "";
    const result = validateEmail(email.trim());
    return result.valid ? "" : (result.error || "Invalid email");
  }, []);

  const handleEmailChange = (value: string) => {
    setNewFriendEmail(value);
    if (value.length > 3) {
      setEmailError(validateEmailInput(value));
    } else {
      setEmailError("");
    }
  };

  // Field validation helper
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'title':
        return value.trim() ? '' : 'Title is required';
      default:
        return '';
    }
  };

  const handleFieldChange = (field: string, value: string, setter: (val: string) => void) => {
    setter(value);
    if (touched[field]) {
      const error = validateField(field, value);
      setFieldErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleFieldBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  // Inline error component
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1.5 text-destructive mt-1.5">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs">{error}</span>
      </div>
    );
  };

  // Step validation
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!title.trim()) {
          setTouched(prev => ({ ...prev, title: true }));
          setFieldErrors(prev => ({ ...prev, title: 'Title is required' }));
          return false;
        }
        if (!targetDate) {
          setTouched(prev => ({ ...prev, targetDate: true }));
          setFieldErrors(prev => ({ ...prev, targetDate: 'Deadline is required' }));
          toast.error("Please select a payment deadline");
          return false;
        }
        return true;
      case 2:
        // If equal split is selected, require friends count
        if (splitType === 'equal' && !friendsToInvite) {
          setTouched(prev => ({ ...prev, friendsToInvite: true }));
          toast.error("Please specify how many friends you're inviting");
          return false;
        }
        if (splitType === 'equal' && parseInt(friendsToInvite) < 1) {
          setTouched(prev => ({ ...prev, friendsToInvite: true }));
          toast.error("You need to invite at least 1 friend");
          return false;
        }
        return true;
      case 3:
        // For equal split, validate that the right number of friends are added
        if (splitType === 'equal' && friendsToInvite) {
          const requiredFriends = parseInt(friendsToInvite);
          if (invitedFriends.length !== requiredFriends) {
            toast.error(`Please add exactly ${requiredFriends} friend(s) (currently ${invitedFriends.length})`);
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setDirection(1);
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const resetForm = () => {
    setCurrentStep(1);
    setTouched({});
    setFieldErrors({});
    setTitle("");
    setDescription("");
    setTargetDate(undefined);
    setTargetAmount("");
    setSplitType("equal");
    setFriendsToInvite("");
    setNewFriendCustomAmount("");
    setInvitedFriends([]);
    setNewFriendEmail("");
    setNewFriendName("");
    setEmailError("");
    setIsOrganizerParticipating(true);
    setInviteLinkEnabled(false);
    setInviteLinkApprovalMode("auto");
    setMaxSelfInvites(null);
  };

  const attemptDialogClose = () => {
    if (hasUnsavedChanges) {
      setShowExitWarning(true);
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleAddFriend = () => {
    if (!newFriendEmail.trim()) {
      setEmailError("Email is required");
      return;
    }
    
    const emailValidation = validateEmail(newFriendEmail.trim());
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || "Invalid email address");
      return;
    }

    if (invitedFriends.some(f => f.email.toLowerCase() === newFriendEmail.toLowerCase().trim())) {
      setEmailError("This email is already added");
      return;
    }

    if (newFriendEmail.toLowerCase().trim() === organizerEmail.toLowerCase()) {
      setEmailError("You can't invite yourself");
      return;
    }

    // For custom split, validate custom amount is provided
    if (splitType === 'custom' && !newFriendCustomAmount) {
      toast.error("Please enter the amount for this friend");
      return;
    }

    const newFriend: InvitedFriend = { 
      email: newFriendEmail.trim(), 
      name: newFriendName.trim() || newFriendEmail.split('@')[0],
      ...(splitType === 'custom' && newFriendCustomAmount ? { customAmount: parseFloat(newFriendCustomAmount) } : {})
    };
    
    setInvitedFriends([...invitedFriends, newFriend]);
    setNewFriendEmail("");
    setNewFriendName("");
    setNewFriendCustomAmount("");
    setEmailError("");
  };

  const handleRemoveFriend = (email: string) => {
    setInvitedFriends(invitedFriends.filter(f => f.email !== email));
  };

  const handleSubmit = async () => {
    // Check if bank details are configured before allowing submission
    if (hasBankDetails === false) {
      setShowBankDetailsDialog(true);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const { error: insertError } = await supabase
        .from("group_purchases")
        .insert({
          organizer_email: organizerEmail,
          organizer_name: organizerName || organizerEmail.split('@')[0],
          title: title.trim(),
          description: description.trim() || null,
          target_date: targetDate ? format(targetDate, 'yyyy-MM-dd') : null,
          target_amount: targetAmount ? parseFloat(targetAmount) : null,
          split_type: splitType,
          payment_type: 'organizer',
          currency: currency,
          invite_link_enabled: inviteLinkEnabled,
          invite_link_approval_mode: inviteLinkApprovalMode,
          max_self_invites: maxSelfInvites,
          category: 'payment',
        });

      if (insertError) throw insertError;

      const { data: purchases, error: fetchError } = await supabase
        .from("group_purchases")
        .select("*")
        .eq("organizer_email", organizerEmail)
        .eq("title", title.trim())
        .order("created_at", { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;
      if (!purchases || purchases.length === 0) throw new Error("Failed to find created payment group");
      
      const purchase = purchases[0];

      // Add organizer as participant if participating
      const participantsToInsert = [];
      if (isOrganizerParticipating) {
        participantsToInsert.push({
          purchase_id: purchase.id,
          email: organizerEmail,
          name: organizerName || organizerEmail.split('@')[0],
          invited_by: organizerEmail,
          role: 'organizer',
        });
      }

      // Add invited friends
      invitedFriends.forEach(friend => {
        participantsToInsert.push({
          purchase_id: purchase.id,
          email: friend.email,
          name: friend.name,
          invited_by: organizerEmail,
        });
      });

      if (participantsToInsert.length > 0) {
        const { error: participantsError } = await supabase
          .from("group_purchase_participants")
          .insert(participantsToInsert);

        // Note: For Group Payments, invitation emails are NOT sent during creation.
        // They will be sent after the organizer configures payment settings (Razorpay/Stripe).
        // This ensures payment links are included in the invitation emails.
        if (participantsError) {
          console.error("Error inserting participants:", participantsError);
        }
      }

      toast.success("Group payment created! Redirecting to discussion...");
      resetForm();
      onOpenChange(false);
      navigate(`/purchases/${purchase.id}`);
    } catch (error: any) {
      console.error("Error creating group payment:", error);
      toast.error("Failed to create group payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={attemptDialogClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Start a Group Payment
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                      currentStep > step.id
                        ? "bg-emerald-600 text-white"
                        : currentStep === step.id
                        ? "bg-emerald-600 text-white"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-1 hidden sm:block",
                    currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-8 sm:w-12 h-0.5 mx-1",
                      currentStep > step.id ? "bg-emerald-600" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Live Summary Sidebar - Compact */}
        {(targetAmount || displayParticipantCount > 0 || splitType === 'custom') && currentStep !== 4 && (
          <div className="p-3 rounded-lg bg-muted/50 border space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Calculator className="w-3.5 h-3.5" />
              Live Summary
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              {targetAmount && (
                <div className="p-2 rounded bg-background">
                  <div className="text-muted-foreground text-xs">Target</div>
                  <div className="font-semibold">{currencySymbol}{parseFloat(targetAmount).toLocaleString()}</div>
                </div>
              )}
              {displayParticipantCount > 0 && (
                <div className="p-2 rounded bg-background">
                  <div className="text-muted-foreground text-xs">People</div>
                  <div className="font-semibold">{displayParticipantCount}</div>
                </div>
              )}
              {splitType === 'equal' && perPersonAmount && (
                <div className="p-2 rounded bg-emerald-500/10">
                  <div className="text-muted-foreground text-xs">Each pays</div>
                  <div className="font-semibold text-emerald-600">{currencySymbol}{perPersonAmount}</div>
                </div>
              )}
              {splitType === 'custom' && customAmountsTotal > 0 && (
                <div className="p-2 rounded bg-emerald-500/10">
                  <div className="text-muted-foreground text-xs">Total assigned</div>
                  <div className="font-semibold text-emerald-600">{currencySymbol}{customAmountsTotal.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step Content with Animation */}
        <div className="relative overflow-hidden min-h-[280px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              className="space-y-5 py-2"
            >
              {/* Step 1: Basics */}
              {currentStep === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="title" className="flex items-center gap-1">
                      What's this payment for?
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="title"
                      placeholder="e.g., Trip to Goa, Office party, Shared rent..."
                      value={title}
                      onChange={(e) => handleFieldChange('title', e.target.value, setTitle)}
                      onBlur={() => handleFieldBlur('title', title)}
                      className={cn(touched.title && fieldErrors.title && "border-destructive")}
                    />
                    <FieldError error={touched.title ? fieldErrors.title : undefined} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description (optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="Add any details about the payment..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Target Date (optional)</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !targetDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {targetDate ? format(targetDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={targetDate}
                          onSelect={(d) => {
                            setTargetDate(d);
                            setCalendarOpen(false);
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Currency Selection */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Payment Currency
                    </Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.name} ({c.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {currency === 'INR' ? 'Payments via Razorpay (UPI, Cards, Netbanking)' : 'International payments via Stripe (coming soon)'}
                    </p>
                  </div>

                </>
              )}

              {/* Step 2: Split - Who & How */}
              {currentStep === 2 && (
                <>
                  {/* Organizer participation - asked first */}
                  <div className="p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="organizer-participating"
                        checked={isOrganizerParticipating}
                        onCheckedChange={(checked) => setIsOrganizerParticipating(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="organizer-participating" className="font-medium cursor-pointer">
                          I'm also contributing to this payment
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Uncheck if you're only organizing and not paying
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>How to split the cost?</Label>
                    <RadioGroup value={splitType} onValueChange={(v) => setSplitType(v as typeof splitType)}>
                      <div className="grid grid-cols-3 gap-2">
                        <div 
                          className={cn(
                            "p-3 rounded-lg border text-center cursor-pointer transition-colors",
                            splitType === 'equal' ? 'border-emerald-500 bg-emerald-500/10' : 'hover:bg-muted/50'
                          )}
                          onClick={() => setSplitType('equal')}
                        >
                          <Calculator className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <div className="text-sm font-medium">Equal</div>
                          <div className="text-xs text-muted-foreground">Same each</div>
                        </div>
                        <div 
                          className={cn(
                            "p-3 rounded-lg border text-center cursor-pointer transition-colors",
                            splitType === 'custom' ? 'border-emerald-500 bg-emerald-500/10' : 'hover:bg-muted/50'
                          )}
                          onClick={() => setSplitType('custom')}
                        >
                          <Users className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <div className="text-sm font-medium">Custom</div>
                          <div className="text-xs text-muted-foreground">Set each</div>
                        </div>
                        <div 
                          className={cn(
                            "p-3 rounded-lg border text-center cursor-pointer transition-colors",
                            splitType === 'open' ? 'border-emerald-500 bg-emerald-500/10' : 'hover:bg-muted/50'
                          )}
                          onClick={() => setSplitType('open')}
                        >
                          <DollarSign className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <div className="text-sm font-medium">Open</div>
                          <div className="text-xs text-muted-foreground">Any amount</div>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Target amount */}
                  <div className="space-y-2">
                    <Label htmlFor="targetAmount">Target Amount {splitType === 'equal' ? '' : '(optional)'}</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currencySymbol}</span>
                      <Input
                        id="targetAmount"
                        type="number"
                        placeholder="0.00"
                        value={targetAmount}
                        onChange={(e) => setTargetAmount(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>

                  {/* Friends count - only shown for equal split */}
                  {splitType === 'equal' && (
                    <div className="space-y-2">
                      <Label htmlFor="friendsToInvite" className="flex items-center gap-1">
                        How many friends are you inviting?
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="friendsToInvite"
                        type="number"
                        min="1"
                        placeholder="e.g., 4"
                        value={friendsToInvite}
                        onChange={(e) => setFriendsToInvite(e.target.value)}
                        className={cn(
                          touched.friendsToInvite && !friendsToInvite && "border-destructive"
                        )}
                      />
                      {friendsToInvite && targetAmount && (
                        <p className="text-sm text-emerald-600 font-medium">
                          {totalParticipantCount} people × {currencySymbol}{perPersonAmount} each = {currencySymbol}{targetAmount}
                        </p>
                      )}
                      {touched.friendsToInvite && splitType === 'equal' && !friendsToInvite && (
                        <div className="flex items-center gap-1.5 text-destructive">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs">Please specify how many friends you're inviting</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      💡 You can configure payment collection (bank details) in the discussion page
                    </p>
                  </div>
                </>
              )}

              {/* Step 3: Invite */}
              {currentStep === 3 && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-emerald-600" />
                        <Label className="text-sm font-medium">Invite Friends</Label>
                      </div>
                      {/* Show friend count indicator for equal split */}
                      {splitType === 'equal' && friendsToInvite && (
                        <Badge 
                          variant={invitedFriends.length === parseInt(friendsToInvite) ? "default" : "secondary"}
                          className={cn(
                            invitedFriends.length === parseInt(friendsToInvite) 
                              ? "bg-emerald-600" 
                              : "bg-amber-500 text-white"
                          )}
                        >
                          {invitedFriends.length} / {friendsToInvite} friends added
                        </Badge>
                      )}
                    </div>

                    {/* Info message for equal split */}
                    {splitType === 'equal' && friendsToInvite && invitedFriends.length !== parseInt(friendsToInvite) && (
                      <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          {invitedFriends.length < parseInt(friendsToInvite)
                            ? `Add ${parseInt(friendsToInvite) - invitedFriends.length} more friend(s)`
                            : `Remove ${invitedFriends.length - parseInt(friendsToInvite)} friend(s)`
                          }
                        </p>
                      </div>
                    )}

                    {/* List of invited friends - show custom amounts for custom split */}
                    {invitedFriends.length > 0 && (
                      <div className="space-y-2 p-2 bg-muted rounded-md border">
                        {invitedFriends.map((friend) => (
                          <div key={friend.email} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 rounded-full text-xs font-medium truncate">
                                {friend.name || friend.email}
                              </div>
                              {splitType === 'custom' && friend.customAmount && (
                                <span className="text-xs text-muted-foreground">
                                  {currencySymbol}{friend.customAmount}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFriend(friend.email)}
                              className="text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Friend's email"
                          value={newFriendEmail}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          className={cn("flex-1", emailError && "border-destructive")}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddFriend}
                          disabled={!newFriendEmail.trim() || (splitType === 'custom' && !newFriendCustomAmount)}
                          className="h-10 px-3 gap-1.5"
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                      <FieldError error={emailError} />
                      <Input
                        placeholder="Friend's name (optional)"
                        value={newFriendName}
                        onChange={(e) => setNewFriendName(e.target.value)}
                      />
                      {/* Custom amount field - only for custom split */}
                      {splitType === 'custom' && (
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{currencySymbol}</span>
                          <Input
                            type="number"
                            placeholder="Amount for this friend"
                            value={newFriendCustomAmount}
                            onChange={(e) => setNewFriendCustomAmount(e.target.value)}
                            className="pl-8"
                          />
                        </div>
                      )}
                    </div>

                    {/* Only show share link option if NOT equal split */}
                    {splitType !== 'equal' && (
                      <InviteLinkSettings
                        enabled={inviteLinkEnabled}
                        onEnabledChange={setInviteLinkEnabled}
                        approvalMode={inviteLinkApprovalMode}
                        onApprovalModeChange={setInviteLinkApprovalMode}
                        maxSelfInvites={maxSelfInvites}
                        onMaxSelfInvitesChange={setMaxSelfInvites}
                      />
                    )}
                  </div>
                </>
              )}

              {/* Step 4: Review */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-emerald-600" />
                      {title}
                    </h3>
                    {description && (
                      <p className="text-sm text-muted-foreground mb-3">{description}</p>
                    )}
                    <div className="space-y-2 text-sm">
                      {targetDate && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Target Date</span>
                          <span>{format(targetDate, "PPP")}</span>
                        </div>
                      )}
                      {targetAmount && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Target Amount</span>
                          <span className="font-medium">{currencySymbol}{parseFloat(targetAmount).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Currency</span>
                        <Badge variant="secondary">{currency}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Split Type</span>
                        <Badge variant="secondary" className="capitalize">{splitType}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Friends Invited</span>
                        <span>{invitedFriends.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Participants</span>
                        <span>{displayParticipantCount} {isOrganizerParticipating ? '(incl. you)' : ''}</span>
                      </div>
                      {perPersonAmount && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Per Person</span>
                          <span className="font-medium text-emerald-600">≈ {currencySymbol}{perPersonAmount}</span>
                        </div>
                      )}
                      {inviteLinkEnabled && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invite Link</span>
                          <Badge variant="outline" className="text-xs">Enabled</Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    Configure payment collection (bank details) in the discussion page
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? attemptDialogClose : handleBack}
            disabled={isSubmitting}
          >
            {currentStep === 1 ? "Cancel" : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </>
            )}
          </Button>

          {currentStep < STEPS.length ? (
            <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isSubmitting ? "Creating..." : "Create Group Payment"}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showExitWarning} onOpenChange={setShowExitWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-center">Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You have unsaved changes. Are you sure you want to close?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExitWarning(false);
                onOpenChange(false);
                resetForm();
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bank Details Dialog - shown when user tries to submit without bank details */}
      <BankDetailsDialog
        open={showBankDetailsDialog}
        onOpenChange={setShowBankDetailsDialog}
        organizerEmail={organizerEmail}
        onSuccess={() => {
          setHasBankDetails(true);
          // Continue with the original submission after bank details are saved
          handleSubmit();
        }}
      />
    </Dialog>
  );
}
